import React, { useRef, useMemo, Suspense, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

const MARBLE_IMAGES = [
    "/gallery/marble-white-livingroom.jpg",
    "/gallery/marble-chandelier.jpg",
    "/gallery/marble-emperador-hall.jpg",
    "/gallery/marble-palace-hall.jpg",
    "/gallery/marble-outdoor-palace.jpg",
    "/gallery/marble-brown-shine.jpg",
    "/gallery/marble-gate-luxury.jpg",
    "/gallery/marble-white-corridor.jpg",
    "/gallery/marble-modern-hall.jpg",
    "/gallery/marble-travertine.jpg",
    "/gallery/marble-white-entrance.jpg",
    "/gallery/marble-colored-border.jpg",
];

// Load textures once at module level - non-suspending
const textureLoader = new THREE.TextureLoader();
const LOADED_TEXTURES = MARBLE_IMAGES.map((src) => {
    const tex = textureLoader.load(src);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
});

// Octahedron face definitions
const OCTA_FACES = [
    [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
    [[0, 1, 0], [-1, 0, 0], [0, 0, 1]],
    [[-1, 0, 0], [0, -1, 0], [0, 0, 1]],
    [[0, -1, 0], [1, 0, 0], [0, 0, 1]],
    [[0, 1, 0], [1, 0, 0], [0, 0, -1]],
    [[-1, 0, 0], [0, 1, 0], [0, 0, -1]],
    [[0, -1, 0], [-1, 0, 0], [0, 0, -1]],
    [[1, 0, 0], [0, -1, 0], [0, 0, -1]],
];

function DiamondFace({ vertices, texture, radius = 1.8 }) {
    const geometry = useMemo(() => {
        const geo = new THREE.BufferGeometry();
        const positions = new Float32Array(
            vertices.flatMap((v) => v.map((c) => c * radius))
        );
        const uvs = new Float32Array([0, 0, 1, 0, 0.5, 1]);
        geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
        geo.computeVertexNormals();
        return geo;
    }, [vertices, radius]);

    return (
        <mesh geometry={geometry}>
            <meshStandardMaterial
                map={texture}
                color="#ffffff"
                emissive="#c9a961"
                emissiveIntensity={0.18}
                metalness={0.3}
                roughness={0.35}
                side={THREE.DoubleSide}
            />
        </mesh>
    );
}

function GoldDiamond({ scale = 1 }) {
    const groupRef = useRef();
    const outerRef = useRef();
    const outerRef2 = useRef();
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const iv = setInterval(() => {
            setOffset((o) => (o + 1) % MARBLE_IMAGES.length);
        }, 3500);
        return () => clearInterval(iv);
    }, []);

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * 0.45;
            groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.22;
        }
        if (outerRef.current) {
            outerRef.current.rotation.y -= delta * 0.2;
            outerRef.current.rotation.z += delta * 0.12;
        }
        if (outerRef2.current) {
            outerRef2.current.rotation.x += delta * 0.15;
            outerRef2.current.rotation.z -= delta * 0.08;
        }
    });

    return (
        <group scale={scale}>
            <group ref={groupRef}>
                {OCTA_FACES.map((verts, i) => (
                    <DiamondFace
                        key={i}
                        vertices={verts}
                        texture={LOADED_TEXTURES[(i + offset) % MARBLE_IMAGES.length]}
                    />
                ))}
                <mesh>
                    <octahedronGeometry args={[1.808, 0]} />
                    <meshBasicMaterial color="#e8c86a" wireframe />
                </mesh>
            </group>
            <mesh ref={outerRef} scale={2.6}>
                <torusGeometry args={[1, 0.012, 16, 100]} />
                <meshBasicMaterial color="#e8c86a" />
            </mesh>
            <mesh ref={outerRef2} scale={3.0}>
                <torusGeometry args={[1, 0.006, 12, 100]} />
                <meshBasicMaterial color="#c9a961" transparent opacity={0.7} />
            </mesh>
        </group>
    );
}

function GoldParticles({ count = 220 }) {
    const meshRef = useRef();
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const positions = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            const r = 3 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            arr.push({
                x: r * Math.sin(phi) * Math.cos(theta),
                y: r * Math.sin(phi) * Math.sin(theta),
                z: r * Math.cos(phi),
                speed: 0.15 + Math.random() * 0.5,
                offset: Math.random() * Math.PI * 2,
                scale: 0.02 + Math.random() * 0.05,
            });
        }
        return arr;
    }, [count]);

    useFrame((state) => {
        if (!meshRef.current) return;
        const t = state.clock.elapsedTime;
        positions.forEach((p, i) => {
            const angle = t * p.speed + p.offset;
            dummy.position.set(
                p.x + Math.sin(angle) * 0.4,
                p.y + Math.cos(angle * 0.8) * 0.4,
                p.z + Math.sin(angle * 0.6) * 0.4,
            );
            dummy.scale.setScalar(p.scale);
            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        });
        meshRef.current.instanceMatrix.needsUpdate = true;
        meshRef.current.rotation.y = t * 0.05;
    });

    return (
        <instancedMesh ref={meshRef} args={[null, null, count]}>
            <sphereGeometry args={[1, 8, 8]} />
            <meshBasicMaterial color="#e8c86a" transparent opacity={0.9} />
        </instancedMesh>
    );
}

function MarblePlate() {
    const ref = useRef();
    const ringRef = useRef();
    const [texIdx, setTexIdx] = useState(3);

    useEffect(() => {
        const iv = setInterval(() => {
            setTexIdx((i) => (i + 1) % MARBLE_IMAGES.length);
        }, 3500);
        return () => clearInterval(iv);
    }, []);

    useFrame((state, delta) => {
        if (ref.current) ref.current.rotation.z += delta * 0.06;
        if (ringRef.current) ringRef.current.rotation.z -= delta * 0.25;
    });

    return (
        <group position={[0, -2.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <mesh ref={ref}>
                <circleGeometry args={[2.6, 64]} />
                <meshStandardMaterial
                    map={LOADED_TEXTURES[texIdx]}
                    color="#ffffff"
                    emissive="#c9a961"
                    emissiveIntensity={0.14}
                    metalness={0.4}
                    roughness={0.3}
                    side={THREE.DoubleSide}
                />
            </mesh>
            <mesh ref={ringRef} position={[0, 0, 0.02]}>
                <ringGeometry args={[2.6, 2.72, 80]} />
                <meshBasicMaterial color="#e8c86a" side={THREE.DoubleSide} />
            </mesh>
            <mesh position={[0, 0, 0.03]}>
                <ringGeometry args={[2.78, 2.82, 80]} />
                <meshBasicMaterial color="#c9a961" transparent opacity={0.6} side={THREE.DoubleSide} />
            </mesh>
        </group>
    );
}

export default function ThreeScene({ variant = "hero" }) {
    const cameraPos = variant === "hero" ? [0, 0.3, 9] : [0, 0, 10];
    const scale = variant === "hero" ? 0.7 : 0.5;
    return (
        <Canvas
            dpr={[1, 1.8]}
            camera={{ position: cameraPos, fov: 45 }}
            gl={{ antialias: true, alpha: true }}
            style={{ background: "transparent" }}
        >
            <Suspense fallback={null}>
                <ambientLight intensity={0.7} />
                <directionalLight position={[5, 5, 5]} intensity={1.6} color="#fff5d0" />
                <directionalLight position={[-5, -3, -5]} intensity={0.8} color="#c9a961" />
                <pointLight position={[0, 3, 3]} intensity={2.5} color="#e8c86a" />
                <pointLight position={[3, -2, 2]} intensity={1.2} color="#f4d789" />

                <Float speed={1.4} rotationIntensity={0.6} floatIntensity={0.9}>
                    <GoldDiamond scale={scale} />
                </Float>

                {variant === "hero" && <MarblePlate />}
                <GoldParticles count={variant === "hero" ? 260 : 150} />
            </Suspense>
        </Canvas>
    );
}
